/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { API_URL } from 'data/services/apiConstants';
import { getMapLinks } from 'utils/propertyLinks';
import { isManhattan } from 'utils/propertyUtils';

import Tooltip from 'components/Tooltip';

import ImagesWidget from 'pages/_rd_overview/mapCard/ImagesWidget';
import LeafletMap from 'pages/_rd_overview/mapCard/LeafletMap';
import SidewalkMap from 'pages/_rd_overview/mapCard/SidewalkMap';

import { openModal } from 'reducers/modalReducer';
import { setMapView } from 'reducers/mapReducer';
import { MODAL_TYPES } from 'view/ModalManager';

import './MapCard.css';
import { get } from 'data/services/apiService';

const maps = [
	'images',
	'1920s',
	'1950s',
	'sanborn',
	'street',
	'zoning',
	'tax',
	'zola',
	'fema',
];

function MapCard({ propertyData, zoningData }) {
	const dispatch = useDispatch();
	const { currentMap, streetViewMode } = useSelector((state) => state.map);
	const [coords, setCoords] = useState({
		latitude: propertyData?.lat,
		longitude: propertyData?.['long'],
	});

	const openIframeModal = (url, title) => {
		dispatch(openModal(MODAL_TYPES.IFRAME, { url, title }));
	};

	const mapInfo = {
		images: {
			label: 'Images',
			iconUrl: `${API_URL}${propertyData?.thumbnail_url}`,
		},
		'1920s': {
			label: '1920s Street',
			iconUrl: '/map_icon_1920s.png',
		},
		'1950s': {
			label: '1950s Street',
			iconUrl: '/map_icon_1950s.png',
		},
		sanborn: {
			label: 'Sanborn',
			iconUrl: '/map_icon_sanborn.png',
		},
		street: {
			label: 'Street',
			iconUrl: '/map_icon_street.png',
		},
		zoning: {
			label: 'Zoning',
			iconUrl: '/map_icon_zoning.png',
			outboundUrl: getMapLinks(propertyData, zoningData)['Zoning Map'],
		},
		tax: {
			label: 'Tax',
			iconUrl: '/map_icon_tax.png',
			outboundUrl: getMapLinks(propertyData, zoningData)['DOF Digital Tax Map'],
		},
		zola: {
			label: 'ZoLa',
			iconUrl: '/map_icon_zola.png',
			outboundUrl: getMapLinks(propertyData, zoningData)['ZoLa Map'],
		},
		fema: {
			label: 'FEMA',
			iconUrl: '/map_icon_fema.png',
			outboundUrl: getMapLinks(propertyData, zoningData)['FEMA Flood Map'],
		},
	};

	const fetchCoords = useCallback(async () => {
		if (propertyData?.address) {
			const address = encodeURIComponent(propertyData.address);
			get({
				url: `https://nominatim.openstreetmap.org/search?q=${address}&format=json`,
				onSuccess: (result) => {
					if (result?.length > 0) {
						setCoords({ latitude: result[0].lat, longitude: result[0].lon });
					}
				},
				onError: (error) => {},
			});
		}
	}, [propertyData?.address]);

	useEffect(() => {
		setCoords({
			latitude: propertyData?.lat,
			longitude: propertyData?.['long'],
		});
	}, [propertyData?.lat, propertyData?.['long']]);

	useEffect(() => {
		if (!coords?.latitude && !coords.longitude && propertyData?.id) {
			fetchCoords();
		}
	}, [coords?.latitude, coords?.longitude, fetchCoords, propertyData?.id]);

	const openSanbornInfoModal = () => {
		dispatch(openModal(MODAL_TYPES.SANBORN_INFO, { propertyData, zoningData }));
	};

	const setMap = (mapName) => {
		if (mapName === 'sanborn' && !isManhattan(propertyData)) {
			openSanbornInfoModal();
			return;
		}

		if (mapInfo[mapName]?.iframeUrl) {
			openIframeModal(
				mapInfo[mapName].iframeUrl,
				`${mapInfo[mapName].label} Map`,
			);
			// window.open(mapInfo[mapName].outboundUrl, '_blank').focus();
		} else if (mapInfo[mapName]?.outboundUrl) {
			// openIframeModal(mapInfo[mapName].outboundUrl, `${mapInfo[mapName].label} Map`)
			window.open(mapInfo[mapName].outboundUrl, '_blank').focus();
		} else {
			dispatch(setMapView(mapName || null, streetViewMode));
		}
	};
	const isDisabled = (mapName) => false;
	const tipText = (mapName) => {
		if (mapName === 'sanborn' && !isManhattan(propertyData)) {
			let tooltipText = "Sanborn maps aren't available outside of Manhattan.";
			const sanbornInfo = [];

			if (zoningData?.sanborn) {
				sanbornInfo.push(`Sanborn Map #: ${zoningData.sanborn}`);
			}
			if (propertyData?.sanborn_volume) {
				sanbornInfo.push(`Sanborn Volume: ${propertyData.sanborn_volume}`);
			}
			if (propertyData?.sanborn_page) {
				sanbornInfo.push(`Sanborn Page: ${propertyData.sanborn_page}`);
			}

			if (sanbornInfo.length > 0) {
				tooltipText += '\n\n' + sanbornInfo.join('\n');
			}

			return tooltipText;
		}
		return mapInfo[mapName].label;
	};

	const MapSelection = (
		<div className="property-images-strip-container">
			<h2 className="property-images-title">Property Images</h2>
			<div className="property-images-strip">
				{maps.map((mapName) => (
					<Tooltip
						disable={!(mapName === 'sanborn' && !isManhattan(propertyData))}
						text={tipText(mapName)}
						key={mapName}
					>
						<button
							className={`property-image-btn ${
								isDisabled(mapName) ? 'is-disabled' : ''
							}`}
							disabled={isDisabled(mapName)}
							onClick={() => setMap(mapName)}
							key={mapName}
						>
							<img
								className="property-image-thumb"
								src={mapInfo[mapName].iconUrl}
								alt={mapInfo[mapName].label}
								onError={(e) => {
									e.target.onerror = null;
									e.target.src = '/map_icon_street.png';
								}}
							/>
							<span className="property-image-label">{mapInfo[mapName].label}</span>
						</button>
					</Tooltip>
				))}
			</div>
		</div>
	);

	let activeContent = null;

	if (currentMap !== null) {
		if (currentMap === 'images') {
			activeContent = (
				<ImagesWidget
					propertyData={propertyData}
					zoningData={zoningData}
					thumbnailUrl={propertyData?.thumbnail_url}
					onBack={() => setMap(null)}
				/>
			);
		} else {
			const mapType =
				currentMap === 'street'
					? `${currentMap}-${streetViewMode}`
					: currentMap;
			activeContent = (
				<div className="map-widget">
					<div className="map-overlay-buttons">
						<button onClick={() => setMap(null)}>
							<i className="bi bi-x-lg" />
						</button>
					</div>
					{currentMap === 'street' && (
						<div className="map-toggle-buttons">
							<button
								className={`toggle-button ${streetViewMode === 'satellite' ? 'active' : ''}`}
								onClick={() => dispatch(setMapView(currentMap, 'satellite'))}
							>
								Satellite
							</button>
							<button
								className={`toggle-button ${streetViewMode === 'sidewalk' ? 'active' : ''}`}
								onClick={() => dispatch(setMapView(currentMap, 'sidewalk'))}
							>
								Sidewalk
							</button>
						</div>
					)}
					{currentMap === 'street' && streetViewMode === 'satellite' && (
						<LeafletMap
							propertyData={propertyData}
							zoningData={zoningData}
							mapType={mapType}
							latitude={coords.latitude}
							longitude={coords.longitude}
						/>
					)}
					{currentMap === 'street' && streetViewMode === 'sidewalk' && (
						<SidewalkMap propertyData={propertyData} />
					)}
					{currentMap !== 'street' && (
						<LeafletMap
							propertyData={propertyData}
							zoningData={zoningData}
							mapType={mapType}
							latitude={coords.latitude}
							longitude={coords.longitude}
						/>
					)}
				</div>
			);
		}
	}

	return (
		<div className="property-images-section-wrapper">
			{MapSelection}
			{activeContent && (
				<div className="property-map-viewer-card mt-3">
					<div className="overview-card-content">{activeContent}</div>
				</div>
			)}
		</div>
	);
}

export default MapCard;
